import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Clock, X, AlertTriangle } from "lucide-react";
import { useSubscription } from "@/hooks/useSubscription";
import { useAuth } from "@/contexts/AuthContext";

const DISMISS_KEY = "trial_banner_dismissed_until";

function daysUntil(date: string): number {
  const ms = new Date(date).getTime() - Date.now();
  return Math.ceil(ms / (1000 * 60 * 60 * 24));
}

export function TrialEndingBanner() {
  const { mode } = useAuth();
  const { subscription, isTrialing, isPastDue } = useSubscription();
  const { pathname } = useLocation();
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    const until = localStorage.getItem(DISMISS_KEY);
    if (until && Date.now() < Number(until)) setDismissed(true);
    else setDismissed(false);
  }, [subscription?.id]);

  // Don't show on landing/login/pricing/legal
  const hide =
    pathname === "/" ||
    pathname === "/login" ||
    pathname.startsWith("/pricing") ||
    pathname.startsWith("/terms") ||
    pathname.startsWith("/refunds") ||
    pathname.startsWith("/privacy") ||
    pathname.startsWith("/csi/") ||
    pathname.startsWith("/unsubscribe") ||
    pathname.startsWith("/email-preview");

  if (hide || mode !== "real" || dismissed) return null;

  // Past-due always shows (no dismissal)
  if (isPastDue) {
    return (
      <div className="w-full bg-destructive/15 border-b border-destructive/30 px-4 py-2 text-sm flex items-center justify-center gap-3 text-foreground">
        <AlertTriangle className="h-4 w-4 text-destructive shrink-0" />
        <span>
          Payment failed — update your card before retries end to keep dashboard access.
        </span>
        <Link to="/settings" className="underline font-medium text-destructive">
          Fix now
        </Link>
      </div>
    );
  }

  if (!isTrialing || !subscription?.current_period_end) return null;

  const days = daysUntil(subscription.current_period_end);
  if (days > 3 || days < 0) return null;

  const dismiss = () => {
    // Re-show next day
    localStorage.setItem(DISMISS_KEY, String(Date.now() + 24 * 60 * 60 * 1000));
    setDismissed(true);
  };

  const label =
    days === 0
      ? "Your trial ends today — your card will be charged."
      : days === 1
        ? "Your trial ends tomorrow — your card will be charged."
        : `Your trial ends in ${days} days — your card will be charged.`;

  return (
    <div className="w-full bg-primary/10 border-b border-primary/30 px-4 py-2 text-sm flex items-center justify-center gap-3 text-foreground">
      <Clock className="h-4 w-4 text-primary shrink-0" />
      <span>{label}</span>
      <Link to="/settings" className="underline font-medium text-primary">
        Manage
      </Link>
      <button onClick={dismiss} aria-label="Dismiss" className="ml-2 opacity-60 hover:opacity-100">
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}
